import { all, call, fork, put, takeEvery, takeLatest, select } from 'redux-saga/effects';

import {
  WS_SUBSCRIBE,
  WS_UNSUBSCRIBE,
  WS_MESSAGE_RECEIVED,
  WS_START_CHECK_CONNECTION,
  WS_STOP_CHECK_CONNECTION,
  WS_FETCH_DATA,
} from '../constants/ActionTypes';
import { getUnreadNotices } from '../actions/Notices';
import { getActiveDiversionsRequest } from '../actions/Diversion';
import {
  WSSubscribe,
  WSMessageReceived,
  WSStopCheckConnection,
  WSStartCheckConnection,
  WSFetchAppData,
  WSUnsubscribe,
} from '../actions/WSNotifications';
import defaultExport, { ALARMS_PER_PAGE } from '../constants/QueriesAndFilters';
import {
  delay,
  PAGE_NUMBER_QUERY_KEY,
  ROWS_PER_PAGE_QUERY_KEY,
  SOCKET_NOTIFICATIONS_TYPES,
  USER_ROLES,
} from '../constants/Values';
import { store } from '../store/instance';
import * as wsNotificationsApi from '../api/WSNotifications';
import Config from '../env.config';
import * as alarmsActions from '../actions/Alarms';
import { getAgendaList, getAgendaDetails } from '../actions/Agenda';
import { getQueryParameter } from '../util/query';

// TODO: replace all store.dispatch with saga's approach. Example how to do it:
// https://github.com/redux-saga/redux-saga/issues/79
/* eslint-disable no-console */

// Reconnect pacing. This used to be one flat 5s delay, which is what made
// educe-tec#284 unrecoverable. A backend restart puts every open console into a
// reconnect loop, and once a handful of handshakes to the host have failed the
// browser starts holding each new one back by a growing, randomised delay that
// plateaus around 5 seconds — the same 5 seconds the server gives a ws token
// (WSAccessTokenDetails.EXPIRATION_PERIOD). Past that point the token is already
// swept from the server's cache when the handshake carrying it lands, the server
// answers 400, and that 400 is itself another failed handshake keeping the
// throttle up. Backing off with jitter keeps us out of that regime, and stops
// every console in the estate retrying on the same beat against a cold JVM.
// Measurements and reproduction: docs/superpowers/FINDING-ws-token-expiry-284.
const RECONNECT_BASE_DELAY = 500;
const RECONNECT_MAX_DELAY = 30000;
const RECONNECT_JITTER = 0.3;

// A handshake that never completes leaves the socket in CONNECTING forever —
// the browser applies no timeout of its own. Set well clear of any legitimate
// connect so this only fires when something is genuinely wedged.
const CONNECT_TIMEOUT = 20000;

const CHECK_WS_CONNECTION_TIMEOUT = 20000;

let socket;
let reconnectAttempts = 0;
let connectTimer;

const userRolesSelector = ({ profile }) => (profile.user && profile.user.roles) || [];
const isAuthorizedSelector = state => state.auth.signedIn;
const agendaOfDaySelector = state => state.agenda.agendaOfDay;
const agendaDetailsSelector = state => state.agenda.agendaDetails;
const currentUserProfileSelector = state => state.profile.user;
const wsEventSubscriptionsSelector = state => state.ws.subscriptions;

let checkConnectionTimer;

function* notifySubscriptions(eventType, payload) {
  const subscriptions = yield select(wsEventSubscriptionsSelector);
  const callbacks = subscriptions[eventType] || [];
  callbacks.forEach(callback => callback(payload));
}

/**
 * Strip every handler off a socket before letting go of it.
 *
 * A replaced socket that keeps its handlers is not inert: its onclose fires long
 * after the run that created it was superseded and starts a reconnect chain of
 * its own. Those orphans are the amplifier behind educe-tec#284 — each one
 * multiplies the rate of failed handshakes, which is exactly what the browser
 * throttles on.
 */
function detachSocket(target) {
  if (!target) {
    return;
  }

  /* eslint-disable no-param-reassign */
  target.onopen = null;
  target.onmessage = null;
  target.onerror = null;
  target.onclose = null;
  /* eslint-enable no-param-reassign */
}

function teardownSocket(target) {
  if (!target) {
    return;
  }

  // Detach BEFORE closing: close() on a socket that is still CONNECTING fires
  // onerror, and an attached handler would read that as a failure and reconnect
  // us straight back.
  detachSocket(target);

  if (target.readyState !== target.CLOSED) {
    target.close();
  }
}

function nextReconnectDelay(attempt) {
  // eslint-disable-next-line no-restricted-properties
  const growth = RECONNECT_BASE_DELAY * Math.pow(2, attempt - 1);
  const backoff = Math.min(RECONNECT_MAX_DELAY, growth);
  const jitter = backoff * RECONNECT_JITTER * (Math.random() * 2 - 1);

  return Math.max(RECONNECT_BASE_DELAY, Math.round(backoff + jitter));
}

function* wsSubscribe() {
  const isAuthorized = yield select(isAuthorizedSelector);

  if (!isAuthorized) {
    return;
  }

  // Already holding a live socket, so there is nothing to do. App and Sos both
  // dispatch WS_SUBSCRIBE on mount; the callers that mean "rebuild it" — the
  // connection watchdog and Sos — dispatch WS_UNSUBSCRIBE first, which clears
  // this and lets the rebuild through.
  if (socket && socket.readyState === socket.OPEN) {
    return;
  }

  // Whatever the previous run left behind goes now, before anything is minted.
  teardownSocket(socket);
  socket = undefined;
  clearTimeout(connectTimer);

  if (reconnectAttempts > 0) {
    yield call(delay, nextReconnectDelay(reconnectAttempts));
  }

  try {
    const { data } = yield call(wsNotificationsApi.getWSToken);

    // The token is only valid for 5 seconds server-side, so the handshake has to
    // go out now. encodeURIComponent because this is base64: a '+' would reach
    // the server as a space, which commons-codec silently skips, shifting every
    // byte after it and producing the other 400 branch.
    const key = window.btoa(JSON.stringify(data));
    socket = new window.WebSocket(`${Config.WS_URL}/ws/events/?request=${encodeURIComponent(key)}`);

    const pending = socket;

    connectTimer = setTimeout(() => {
      if (pending.readyState === pending.CONNECTING) {
        console.log('Sockets handshake timed out');
        teardownSocket(pending);
        if (socket === pending) {
          socket = undefined;
        }
        reconnectAttempts += 1;
        store.dispatch(WSSubscribe());
      }
    }, CONNECT_TIMEOUT);

    pending.onopen = () => {
      console.log('Sockets connection established');
      clearTimeout(connectTimer);
      reconnectAttempts = 0;
      all([store.dispatch(WSStartCheckConnection()), store.dispatch(WSFetchAppData())]);
    };

    pending.onmessage = event => {
      const eventData = event.data ? JSON.parse(event.data) : null;
      if (eventData.event === 'ALIVE_CONNECTION') {
        store.dispatch(WSMessageReceived(eventData));
        return;
      }
      console.log('--------------------');
      console.log(`Message received: ${JSON.stringify(eventData)}`);
      store.dispatch(WSMessageReceived(eventData));
    };

    // onerror is advisory. A rejected handshake fires error and THEN close, but a
    // server that drops an established socket fires close and no error at all —
    // which is precisely what a backend restart does. Reconnecting from onerror
    // alone therefore left the console silent until the 20s watchdog noticed.
    // close is the one event every failure path delivers, so close owns recovery.
    pending.onerror = error => {
      console.log('Sockets connection error');
      console.log(JSON.stringify(error));
    };

    pending.onclose = () => {
      clearTimeout(connectTimer);
      detachSocket(pending);
      if (socket === pending) {
        socket = undefined;
      }
      reconnectAttempts += 1;
      store.dispatch(WSSubscribe());
    };
  } catch (error) {
    console.log(JSON.stringify(error));
    reconnectAttempts += 1;
    yield put(WSSubscribe());
  }
}

function* wsUnsubscribe() {
  // A deliberate teardown — sign-out, page unload, or a watchdog rebuild. Detach
  // first so onclose does not read it as a failure and reconnect us.
  teardownSocket(socket);
  socket = undefined;
  clearTimeout(connectTimer);
  reconnectAttempts = 0;

  yield put(WSStopCheckConnection());
}

function* messageReceived({ payload }) {
  const userRoles = yield select(userRolesSelector);

  if (payload.event !== SOCKET_NOTIFICATIONS_TYPES.ALIVE_CONNECTION) {
    console.log('payload: ', payload);
  }

  if (payload) {
    switch (payload.event) {
      case SOCKET_NOTIFICATIONS_TYPES.UPDATE_ROUTE: {
        if (
          userRoles.includes(USER_ROLES.ADMIN) ||
          userRoles.includes(USER_ROLES.TM) ||
          userRoles.includes(USER_ROLES.COMMUNICATION) ||
          userRoles.includes(USER_ROLES.SECURITY) ||
          userRoles.includes(USER_ROLES.CTM)
        ) {
          yield notifySubscriptions(payload.event, payload);
          // Refetch active diversions on route updates for TM-tier roles.
          yield put(getActiveDiversionsRequest());
        } else if (userRoles.includes(USER_ROLES.STUDENT)) {
          // const agendaOfDaySelector = state => state.agenda.agendaOfDay;
          // const agendaDetailsSelector = state => state.agenda.agendaDetails;
          // TODO: re-fetch agenda list or agenda details
          const agendaOfDay = yield select(agendaOfDaySelector);

          if (agendaOfDay && agendaOfDay.date) {
            const profile = yield select(currentUserProfileSelector);
            if (profile) {
              yield put(getAgendaList(profile.userId, agendaOfDay.date));
            }
          }

          const agendaDetails = yield select(agendaDetailsSelector);

          if (
            agendaDetails &&
            agendaDetails.data &&
            Array.isArray(payload.ids) &&
            payload.ids.includes(agendaDetails.data.routeId)
          ) {
            const profile = yield select(currentUserProfileSelector);
            if (profile) {
              yield put(
                getAgendaDetails(profile.userId, agendaDetails.date, agendaDetails.data.routeId),
              );
            }
          }
        }

        break;
      }
      case SOCKET_NOTIFICATIONS_TYPES.UPDATE_PASSENGER_ROUTE: {
        if (
          userRoles.includes(USER_ROLES.ADMIN) ||
          userRoles.includes(USER_ROLES.TM) ||
          userRoles.includes(USER_ROLES.COMMUNICATION) ||
          userRoles.includes(USER_ROLES.SECURITY) ||
          userRoles.includes(USER_ROLES.CTM) ||
          userRoles.includes(USER_ROLES.STUDENT)
        ) {
          yield notifySubscriptions(payload.event, payload);
        }

        break;
      }
      case SOCKET_NOTIFICATIONS_TYPES.UPDATE_ALARMS_COUNT: {
        if (
          userRoles.includes(USER_ROLES.ADMIN) ||
          userRoles.includes(USER_ROLES.TM) ||
          userRoles.includes(USER_ROLES.COMMUNICATION) ||
          userRoles.includes(USER_ROLES.SECURITY) ||
          userRoles.includes(USER_ROLES.CTM)
        ) {
          yield put(alarmsActions.getActiveAlarmsCount());
          yield put(
            alarmsActions.getAlarmList({
              pagination: {
                pageNumber: Number(getQueryParameter(PAGE_NUMBER_QUERY_KEY)) || 1,
                size: Number(getQueryParameter(ROWS_PER_PAGE_QUERY_KEY)) || ALARMS_PER_PAGE,
                sort: defaultExport.ActiveAlarms.SORT,
              },
            }),
          );
        }
        break;
      }
      case SOCKET_NOTIFICATIONS_TYPES.UPDATE_NOTIFICATION: {
        // we should re-fetch notifications count in case of user receive new notification
        if (
          userRoles.includes(USER_ROLES.ADMIN) ||
          userRoles.includes(USER_ROLES.TM) ||
          userRoles.includes(USER_ROLES.COMMUNICATION) ||
          userRoles.includes(USER_ROLES.SECURITY) ||
          userRoles.includes(USER_ROLES.CTM) ||
          userRoles.includes(USER_ROLES.STUDENT)
        ) {
          yield put(getUnreadNotices());
        }

        break;
      }

      case SOCKET_NOTIFICATIONS_TYPES.ALIVE_CONNECTION: {
        if (payload.ids) {
          store.dispatch(WSStartCheckConnection());
        }

        break;
      }

      case SOCKET_NOTIFICATIONS_TYPES.REFRESH_OVERVIEW: {
        if (
          userRoles.includes(USER_ROLES.ADMIN) ||
          userRoles.includes(USER_ROLES.TM) ||
          userRoles.includes(USER_ROLES.COMMUNICATION) ||
          userRoles.includes(USER_ROLES.SECURITY) ||
          userRoles.includes(USER_ROLES.CTM)
        ) {
          yield notifySubscriptions(payload.event, payload);
        } else if (userRoles.includes(USER_ROLES.STUDENT)) {
          const agendaOfDay = yield select(agendaOfDaySelector);

          if (agendaOfDay && agendaOfDay.date) {
            const profile = yield select(currentUserProfileSelector);
            if (profile) {
              yield put(getAgendaList(profile.userId, agendaOfDay.date));
            }
          }
        }

        break;
      }
      // Manage Routes V2's freshness bridge (spec 5.3). Pure fan-out: the whole
      // frame goes through untouched because the source lives in `payload.ids`
      // (`['OPS'] | ['IMPORT'] | ['ACTIVATION', id]`) and the section derives it.
      // Gated to the two roles that can open the section (ADMIN + TM), matching the
      // REFRESH_OVERVIEW precedent above.
      //
      // The ops emitter now adds `seq`, `originator` and `ops` to the same frame
      // (delta-sync spec §3.1) and this case needs NO change to carry them —
      // forwarding the whole payload is what makes that additive. `ids` keeps its
      // exact meaning as the discriminator, so this fan-out stays correct for
      // every emitter, with or without the delta fields.
      //
      // ERRATUM (QA build #36). That paragraph was true of THIS case and was read
      // as if it were true of the path. It is not: `payload` is whatever
      // `actions/WSNotifications.js` built, and `WSMessageReceived` was rebuilding
      // it from `{event, ids}` alone — so the delta fields were already gone before
      // this line ran, and every peer edit fell back to a refetch. "This case needs
      // no change" only means something once the CREATOR forwards the frame whole,
      // which it now does. Pinned by `WSNotifications.spec.js`'s wire-frame test.
      case SOCKET_NOTIFICATIONS_TYPES.ROUTES_DATASET_CHANGED: {
        if (userRoles.includes(USER_ROLES.ADMIN) || userRoles.includes(USER_ROLES.TM)) {
          yield notifySubscriptions(payload.event, payload);
        }
        break;
      }
      case SOCKET_NOTIFICATIONS_TYPES.UPDATE_ECA_EVENT: {
        if (
          userRoles.includes(USER_ROLES.ADMIN) ||
          userRoles.includes(USER_ROLES.ECA_MANAGER) ||
          userRoles.includes(USER_ROLES.ECA_ACTIVITY_LEAD)
        ) {
          yield notifySubscriptions(payload.event, payload);
        }
        break;
      }
      default:
        break;
    }
  }
}

function* sleepCheckConnection(time) {
  yield new Promise(resolve => {
    checkConnectionTimer = setTimeout(resolve, time);
  });
}

function* stopCheckConnection() {
  yield new Promise(resolve => {
    clearTimeout(checkConnectionTimer);
    resolve();
  });
}

function* startCheckConnection() {
  const isAuthorized = yield select(isAuthorizedSelector);

  if (isAuthorized) {
    yield call(stopCheckConnection);
    yield call(sleepCheckConnection, CHECK_WS_CONNECTION_TIMEOUT);
    console.log('-----------------');
    console.log('start re-subscribe');
    yield put(WSUnsubscribe());
    yield put(WSSubscribe());
  }
}

function* fetchDataOnConnect() {
  yield Object.values(SOCKET_NOTIFICATIONS_TYPES).map(TYPE =>
    store.dispatch(WSMessageReceived({ event: TYPE })),
  ); //eslint-disable-line
}

export function* wsSubscribeSaga() {
  // takeLatest, not takeEvery: concurrent runs each overwrote the module-level
  // socket and orphaned the previous one, so a burst of WS_SUBSCRIBE turned into
  // a growing fleet of sockets all racing to reconnect. One run at a time.
  yield takeLatest(WS_SUBSCRIBE, wsSubscribe);
}

export function* wsUnsubscribeSaga() {
  yield takeEvery(WS_UNSUBSCRIBE, wsUnsubscribe);
}

export function* messageReceivedSaga() {
  yield takeEvery(WS_MESSAGE_RECEIVED, messageReceived);
}

export function* startCheckConnectionSaga() {
  yield takeEvery(WS_START_CHECK_CONNECTION, startCheckConnection);
}

export function* stopCheckConnectionSaga() {
  yield takeEvery(WS_STOP_CHECK_CONNECTION, stopCheckConnection);
}

export function* fetchDataOnConnectSaga() {
  yield takeEvery(WS_FETCH_DATA, fetchDataOnConnect);
}

export default function* rootSaga() {
  yield all([
    fork(wsSubscribeSaga),
    fork(wsUnsubscribeSaga),
    fork(messageReceivedSaga),
    fork(startCheckConnectionSaga),
    fork(stopCheckConnectionSaga),
    fork(fetchDataOnConnectSaga),
  ]);
}
